import { BrowserRouter, useLocation } from 'react-router-dom';
import { AppRoutes } from './router';
import { AuthProvider } from './contexts/AuthContext';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/** Maps i18n language codes → Open Graph locale strings */
const OG_LOCALE_MAP: Record<string, string> = {
  en: 'en_US',
  de: 'de_DE',
  fr: 'fr_FR',
  ar: 'ar_SA',
  ru: 'ru_RU',
};

/** Upserts a <meta> tag by attribute selector */
function setMeta(attrName: string, attrValue: string, content: string) {
  let el = document.querySelector(
    `meta[${attrName}="${attrValue}"]`,
  ) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/**
 * Runs at the App level — updates og:locale (and a few static OG tags)
 * whenever the active language changes. No per-page wiring needed.
 */
function OgLocaleSync() {
  const { i18n } = useTranslation();

  useEffect(() => {
    const lang = i18n.language;
    const ogLocale = OG_LOCALE_MAP[lang] ?? 'en_US';

    setMeta('property', 'og:locale', ogLocale);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', 'Health Travel Türkiye');

    // Also keep <html lang="…"> in sync
    document.documentElement.lang = lang;
  }, [i18n.language]);

  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter basename={__BASE_PATH__}>
        <OgLocaleSync />
        <ScrollToTop />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
